import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Plus } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";

export const F1_DEFAULTS = {
  categoria_nova: "Ferramentas", voltagem: "", horimetro: "", numero_ca: "", validade_ca: "",
  data_ultima_inspecao: "", imei: "", local_fixo: "", localizacao_tipo: "", local_id: "",
  contrato_id: "", colaborador_id: "", fornecedor_manutencao_id: "",
  responsavel_local_id: "", responsavel_local_nome: "", posicao: "",
};

export function CategoriaSelect({ value, onChange, disabled }) {
  const [cats, setCats] = useState([]);
  const [novaOpen, setNovaOpen] = useState(false);
  const [novoNome, setNovoNome] = useState("");
  const load = () => axios.get(`${API}/patrimonio-categorias`).then(r => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const criar = async () => {
    try {
      const r = await axios.post(`${API}/patrimonio-categorias`, { nome: novoNome });
      toast.success(`Categoria "${r.data.nome}" criada`);
      setNovaOpen(false); setNovoNome("");
      await load();
      onChange(r.data.nome, []);
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao criar categoria")); }
  };

  return (
    <div className="space-y-1.5">
      <NativeSelect
        value={value}
        onValueChange={(v) => {
          if (v === "__nova__") { setNovaOpen(true); return; }
          const cat = cats.find(c => c.nome === v);
          onChange(v, cat?.campos_extras || []);
        }}
        options={[...cats.map(c => ({ value: c.nome, label: c.nome })), { value: "__nova__", label: "+ Incluir outra" }]}
        disabled={disabled}
        data-testid="select-categoria-patrimonio"
      />
      {novaOpen && (
        <div className="flex gap-2">
          <Input value={novoNome} onChange={(e) => setNovoNome(e.target.value)} placeholder="Nome da nova categoria" autoFocus data-testid="input-nova-categoria" />
          <Button type="button" size="sm" onClick={criar} disabled={novoNome.trim().length < 2} data-testid="btn-salvar-categoria">
            <Plus className="h-4 w-4" />
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setNovaOpen(false)}>✕</Button>
        </div>
      )}
    </div>
  );
}

export function CamposExtras({ camposExtras, formData, setFormData }) {
  if (!camposExtras?.length) return null;
  return (
    <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-slate-50 border" data-testid="campos-extras-categoria">
      {camposExtras.map((ce) => (
        <div key={ce.campo} className="space-y-1">
          <Label className="text-xs">{ce.label}{ce.obrigatorio ? " *" : ""}</Label>
          <Input
            type={ce.tipo === "date" ? "date" : "text"}
            value={formData[ce.campo] || ""}
            onChange={(e) => setFormData({ ...formData, [ce.campo]: e.target.value })}
            data-testid={`input-extra-${ce.campo}`}
          />
        </div>
      ))}
    </div>
  );
}

const TIPOS_LOC = [
  { value: "deposito_central", label: "Depósito Central" },
  { value: "em_obra", label: "Em obra" },
  { value: "com_colaborador", label: "Com colaborador (cautela)" },
  { value: "no_veiculo", label: "No veículo" },
  { value: "escritorio", label: "Escritório" },
  { value: "em_manutencao", label: "Em manutenção" },
];

export function LocalizacaoInicial({ formData, setFormData }) {
  const [obras, setObras] = useState([]);
  const [funcs, setFuncs] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const tipo = formData.localizacao_tipo;

  useEffect(() => {
    if (tipo === "em_obra" && !obras.length)
      axios.get(`${API}/estoque/obras-ativas`).then(r => setObras(r.data || [])).catch(() => {});
    if ((tipo === "com_colaborador" || true) && !funcs.length)
      axios.get(`${API}/funcionarios`).then(r => setFuncs((r.data || []).map(f => ({ id: f.id, nome: f.nome })))).catch(() => {});
    if (tipo === "no_veiculo" && !veiculos.length)
      axios.get(`${API}/locais`, { params: { tipo: "veiculo" } }).then(r => setVeiculos(r.data || [])).catch(() => {});
    if (tipo === "em_manutencao" && !fornecedores.length)
      axios.get(`${API}/clientes-fornecedores`).then(r => setFornecedores((r.data || []).map(f => ({ id: f.id, nome: f.nome })))).catch(() => {});
  }, [tipo]); // eslint-disable-line

  const criarVeiculo = async () => {
    const nome = window.prompt("Nome do veículo (ex: Fiorino ABC-1234):");
    if (!nome) return;
    try {
      const r = await axios.post(`${API}/locais`, { nome, tipo: "veiculo" });
      setVeiculos(v => [...v, r.data]);
      setFormData({ ...formData, local_id: r.data.id });
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao criar veículo")); }
  };

  return (
    <div className="p-3 rounded-lg border border-luvcorp-navy/20 bg-blue-50/40 space-y-3" data-testid="bloco-localizacao-inicial">
      <Label className="font-semibold text-luvcorp-navy">Localização inicial *</Label>
      <div className="space-y-1">
        <Label className="text-xs">Onde o item está agora? *</Label>
        <NativeSelect
          value={tipo}
          onValueChange={(v) => setFormData({ ...formData, localizacao_tipo: v, contrato_id: "", colaborador_id: "", local_id: "", fornecedor_manutencao_id: "" })}
          placeholder="Selecione…"
          options={TIPOS_LOC}
          data-testid="select-localizacao-tipo"
        />
      </div>
      {tipo === "em_obra" && (
        <div className="space-y-1">
          <Label className="text-xs">Obra (contratos ativos) *</Label>
          <NativeSelect value={formData.contrato_id} onValueChange={(v) => setFormData({ ...formData, contrato_id: v })}
            placeholder="Selecione a obra…"
            options={obras.map(o => ({ value: o.id, label: o.cliente_nome ? `${o.titulo} — ${o.cliente_nome}` : o.titulo }))}
            data-testid="select-loc-obra" />
        </div>
      )}
      {tipo === "com_colaborador" && (
        <div className="space-y-1">
          <Label className="text-xs">Colaborador *</Label>
          <NativeSelect value={formData.colaborador_id} onValueChange={(v) => setFormData({ ...formData, colaborador_id: v })}
            placeholder="Selecione…" options={funcs.map(f => ({ value: f.id, label: f.nome }))} data-testid="select-loc-colaborador" />
        </div>
      )}
      {tipo === "no_veiculo" && (
        <div className="space-y-1">
          <Label className="text-xs">Veículo *</Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <NativeSelect value={formData.local_id} onValueChange={(v) => setFormData({ ...formData, local_id: v })}
                placeholder="Selecione…" options={veiculos.map(v => ({ value: v.id, label: v.nome }))} data-testid="select-loc-veiculo" />
            </div>
            <Button type="button" size="sm" variant="outline" onClick={criarVeiculo}><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
      {tipo === "em_manutencao" && (
        <div className="space-y-1">
          <Label className="text-xs">Fornecedor da manutenção *</Label>
          <NativeSelect value={formData.fornecedor_manutencao_id} onValueChange={(v) => setFormData({ ...formData, fornecedor_manutencao_id: v })}
            placeholder="Selecione…" options={fornecedores.map(f => ({ value: f.id, label: f.nome }))} data-testid="select-loc-fornecedor" />
        </div>
      )}
      {tipo && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Responsável no local</Label>
            <NativeSelect value={formData.responsavel_local_id}
              onValueChange={(v) => setFormData({ ...formData, responsavel_local_id: v })}
              placeholder="Funcionário (opcional)…"
              options={[{ value: "", label: "— nome livre abaixo —" }, ...funcs.map(f => ({ value: f.id, label: f.nome }))]}
              data-testid="select-loc-responsavel" />
            {!formData.responsavel_local_id && (
              <Input className="mt-1" placeholder="ou nome livre (sem login)"
                value={formData.responsavel_local_nome}
                onChange={(e) => setFormData({ ...formData, responsavel_local_nome: e.target.value })}
                data-testid="input-loc-responsavel-nome" />
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Prateleira/posição (opcional)</Label>
            <Input placeholder={'Ex: "Galpão A · P3"'} value={formData.posicao}
              onChange={(e) => setFormData({ ...formData, posicao: e.target.value })} data-testid="input-loc-posicao" />
          </div>
        </div>
      )}
    </div>
  );
}
